import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, ChevronRight, Circle, FileEdit, Loader2, Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
const statusConfig = {
    unchanged: {
        label: 'Unchanged',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        icon: Circle
    },
    added: {
        label: 'Added',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        icon: Plus
    },
    modified: {
        label: 'Modified',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        icon: FileEdit
    },
    removed: {
        label: 'Removed',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        icon: Minus
    }
};
export function MasterMatrixTab({ projectId }) {
    const [selectedStatuses, setSelectedStatuses] = useState(new Set());
    const [expandedReqs, setExpandedReqs] = useState(new Set());
    const { data, isLoading, error } = useQuery({
        queryKey: [
            'master-matrix',
            projectId
        ],
        queryFn: async ()=>proposalProjectApiConnect.getMasterMatrix(projectId),
        retry: 1
    });
    const toggleExpand = (id)=>{
        const newExpanded = new Set(expandedReqs);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedReqs(newExpanded);
    };
    const toggleStatus = (status)=>{
        const newStatuses = new Set(selectedStatuses);
        if (newStatuses.has(status)) {
            newStatuses.delete(status);
        } else {
            newStatuses.add(status);
        }
        setSelectedStatuses(newStatuses);
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "w-8 h-8 animate-spin text-blue-500"
            })
        });
    }
    if (error || !data) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-center text-gray-500",
                children: [
                    /*#__PURE__*/ _jsx(AlertCircle, {
                        className: "w-12 h-12 mx-auto mb-4 text-gray-300"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "No master matrix available"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm mt-2",
                        children: "Process multiple documents to track amendments"
                    })
                ]
            })
        });
    }
    const filteredRequirements = selectedStatuses.size > 0 ? data.requirements.filter((req)=>selectedStatuses.has(req.amendmentStatus)) : data.requirements;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-5 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-bold",
                                children: data.summary.total
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-600",
                                children: "Total Requirements"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-gray-50 border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-bold text-gray-600",
                                children: data.summary.unchanged
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-600",
                                children: "Unchanged"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-green-50 border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-bold text-green-600",
                                children: data.summary.added
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-green-700",
                                children: "Added"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-blue-50 border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-bold text-blue-600",
                                children: data.summary.modified
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-blue-700",
                                children: "Modified"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-red-50 border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-bold text-red-600",
                                children: data.summary.removed
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-red-700",
                                children: "Removed"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm text-gray-600 py-2",
                        children: "Filter by status:"
                    }),
                    Object.entries(statusConfig).map(([status, config])=>{
                        const Icon = config.icon;
                        const isSelected = selectedStatuses.has(status);
                        return /*#__PURE__*/ _jsxs("button", {
                            onClick: ()=>toggleStatus(status),
                            className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${isSelected ? `${config.bgColor} ${config.color} border-current` : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`,
                            children: [
                                /*#__PURE__*/ _jsx(Icon, {
                                    className: "w-4 h-4"
                                }),
                                config.label
                            ]
                        }, status);
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3",
                children: [
                    filteredRequirements.map((req)=>{
                        const config = statusConfig[req.amendmentStatus];
                        const Icon = config.icon;
                        const isExpanded = expandedReqs.has(req.id);
                        const hasHistory = req.previousVersions && req.previousVersions.length > 0;
                        return /*#__PURE__*/ _jsx("div", {
                            className: "bg-white border rounded-lg overflow-hidden",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "p-4",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-start gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `mt-0.5 ${config.color}`,
                                                children: /*#__PURE__*/ _jsx(Icon, {
                                                    className: "w-5 h-5"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex-1 min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-start justify-between gap-4 mb-2",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm",
                                                                children: req.text
                                                            }),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: `flex-shrink-0 px-2 py-1 rounded text-xs font-medium ${config.bgColor} ${config.color}`,
                                                                children: config.label
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center gap-4 text-xs text-gray-500",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Introduced in: ",
                                                                    req.introducedIn ?? '—'
                                                                ]
                                                            }),
                                                            req.modifiedIn?.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Modified by: ",
                                                                    req.modifiedIn.join(', ')
                                                                ]
                                                            }),
                                                            req.currentSource?.filename && /*#__PURE__*/ _jsxs("span", {
                                                                className: "ml-auto",
                                                                children: [
                                                                    "Source: ",
                                                                    req.currentSource.filename
                                                                ]
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            hasHistory && /*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>toggleExpand(req.id),
                                                className: "flex-shrink-0 p-1 hover:bg-gray-100 rounded",
                                                children: isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                                    className: "w-4 h-4 text-gray-400"
                                                }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                                    className: "w-4 h-4 text-gray-400"
                                                })
                                            })
                                        ]
                                    }),
                                    hasHistory && isExpanded && /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-4 pt-4 border-t space-y-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-sm font-medium text-gray-700",
                                                children: "Version History"
                                            }),
                                            req.previousVersions.map((version, idx)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "bg-gray-50 rounded p-3 text-sm",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center justify-between mb-2",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium text-gray-700",
                                                                    children: version.filename
                                                                }),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        Math.round(version.similarityScore * 100),
                                                                        "% similar"
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-gray-600",
                                                            children: version.text
                                                        })
                                                    ]
                                                }, idx))
                                        ]
                                    })
                                ]
                            })
                        }, req.id);
                    }),
                    filteredRequirements.length === 0 && /*#__PURE__*/ _jsx("div", {
                        className: "text-center text-gray-500 py-12",
                        children: /*#__PURE__*/ _jsx("p", {
                            children: "No requirements match the selected filters"
                        })
                    })
                ]
            })
        ]
    });
}
